import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router';
import { Button } from '@llura/ui';

import { CoachAvatar } from '../../shared/coach-avatar';
import { useCoachStore } from '../../stores/coach.store';
import { useStrings } from '../../hooks/useStrings';
import {
  useProgressStore,
  useTodayArchetype,
} from '../../stores/progress.store';
import { useJournalStore } from '../../stores/journal.store';
import { selectNextGuidance } from '../../lib/adaptive-coach';
import { useArchetypeTheme } from '../../hooks/useArchetypeTheme';
import { MoodDial } from './mood-dial';
import { PleasureRings } from './pleasure-rings';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * "Your Space" — the home view after coach configuration.
 *
 * Top → coach card + today's adaptive guidance (or today's archetype affirmation).
 * Middle → the Mood Dial: 5-segment selector for today's archetype.
 * Below → Pleasure Rings: daily Mind / Muscle / Sensation progress.
 * Bottom → "Begin ritual" CTA that routes into the workout for the selected archetype.
 */
export function YourSpaceView() {
  const navigate = useNavigate();
  const { strings, interp } = useStrings();
  const { name, avatarUrl, tone, dailyCommand: fallbackCommand } = useCoachStore();
  const history = useProgressStore((s) => s.history);
  const dailyRings = useProgressStore((s) => s.dailyRings);
  const setTodayArchetype = useProgressStore((s) => s.setTodayArchetype);
  const journalEntries = useJournalStore((s) => s.entries);
  const selectedArchetypeId = useTodayArchetype();
  const theme = useArchetypeTheme(selectedArchetypeId);

  const baseGuidance = useMemo(() => {
    if (!tone) return fallbackCommand;
    const cutoff = Date.now() - SEVEN_DAYS_MS;
    const recentSessionCount = history.filter((h) => h.at >= cutoff).length;
    const recentJournalCount = journalEntries.filter((j) => j.at >= cutoff).length;
    const lastMood = journalEntries[0]?.mood ?? null;
    return selectNextGuidance(strings, tone, {
      recentSessionCount,
      recentJournalCount,
      lastMood,
    });
  }, [tone, fallbackCommand, history, journalEntries, strings]);

  const dailyCommand = selectedArchetypeId
    ? strings.archetypes[selectedArchetypeId].affirmation
    : baseGuidance;

  const guidanceLabel = name
    ? interp(strings.yourSpace.todaysGuidanceFrom, { name })
    : strings.yourSpace.todaysGuidanceFrom.replace(/\s*\{name\}\s*$/, '');

  const beginRitual = () => {
    const target = selectedArchetypeId ?? 'priestess';
    navigate(`/workout/${target}`);
  };

  return (
    <div
      className="mx-auto w-full max-w-md space-y-8 px-4 py-6"
      style={theme.style}
      data-archetype={theme.dataArchetype}
    >
      <header className="flex flex-col items-center gap-4 text-center">
        <p className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
          {strings.yourSpace.title}
        </p>
        <CoachAvatar name={name} src={avatarUrl} size={72} glow />
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {guidanceLabel}
          </div>
          {dailyCommand && (
            <motion.p
              key={dailyCommand}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto max-w-xs text-balance font-serif text-xl leading-snug text-foreground"
            >
              “{dailyCommand}”
            </motion.p>
          )}
        </div>
      </header>

      <PleasureRings rings={dailyRings} />

      <MoodDial selected={selectedArchetypeId} onSelect={setTodayArchetype} />

      <div className="space-y-3 pt-2">
        <Button
          type="button"
          onClick={beginRitual}
          disabled={!selectedArchetypeId}
          className="w-full"
          size="lg"
        >
          {strings.yourSpace.beginRitualCta}
        </Button>
        {!selectedArchetypeId && (
          <p className="text-center text-xs text-muted-foreground">
            {strings.yourSpace.beginRitualHint}
          </p>
        )}
        <div className="flex justify-center">
          <Link
            to="/mood-history"
            className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            {strings.yourSpace.historyCta}
          </Link>
        </div>
      </div>
    </div>
  );
}
